import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiUser, FiPhone, FiMapPin, FiCalendar, FiBookOpen, FiCornerDownRight, FiCompass } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MiniCalendar from '../../components/MiniCalendar';

const API_URL = 'https://app.thundermaxhome.com/api.php';

// ตั้งค่าไอคอนหมุดแผนที่เหมือนฝั่งแอปช่าง
const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

// ฟังก์ชันดักจับเหตุการณ์บนแผนที่ (คลิกเพื่อวางหมุด)
function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    }
  });

  return position === null ? null : (
    <Marker position={position} icon={markerIcon} draggable={true} eventHandlers={{ dragend: (e) => setPosition(e.target.getLatLng()) }} />
  );
}

export default function BookingPage() {
  const navigate = useNavigate();
  
  // โหลดพารามิเตอร์รหัสแนะนำจาก URL (เช่น ?ref=THM-NAME หรือ ?referral=THM-NAME)
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const refCode = queryParams.get('ref') || queryParams.get('referral');
    if (refCode) {
      setReferredByTech(refCode);
      setIsFromLink(true);
      Swal.fire({
        title: 'ตรวจพบรหัสผู้แนะนำ',
        html: `คุณได้รับสิทธิ์แนะนำจากช่าง <b>${refCode}</b> เรียบร้อยแล้วครับ`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    }
  }, []);

  // บันทึกเบอร์จากประวัติเดิม (ถ้ามี)
  useEffect(() => {
    const savedPhone = localStorage.getItem('customerPhone');
    if (savedPhone && !phone) {
      setPhone(savedPhone);
    }
  }, []);

  // ดึงวันคิวเต็มจากหลังบ้านเพื่อใช้แจ้งเตือน
  const [blockedDates, setBlockedDates] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}?action=get_blocked_dates`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const dates = (data.data || []).map(item => item.blocked_date);
          setBlockedDates(dates);
        }
      })
      .catch(err => console.error("Error fetching blocked dates:", err));
  }, []);

  // Form states
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [problem, setProblem] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [referredByTech, setReferredByTech] = useState('');
  
  const [isFromLink, setIsFromLink] = useState(false);
  const [mapPosition, setMapPosition] = useState({ lat: 13.7563, lng: 100.5018 }); // พิกัดตั้งต้น (กทม.)
  const [hasPinned, setHasPinned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);



  // ดึงตำแหน่งพิกัดปัจจุบันจาก GPS
  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setMapPosition(latLng);
          setHasPinned(true);
          Swal.fire({
            icon: 'success',
            title: 'ดึงตำแหน่งปัจจุบันสำเร็จ',
            showConfirmButton: false,
            timer: 1200
          });
        },
        (error) => {
          console.error(error);
          Swal.fire('ไม่สามารถเข้าถึงตำแหน่งได้', 'กรุณากดอนุญาตสิทธิ์การเข้าถึงตำแหน่ง GPS หรือเลือกจุดในแผนที่เองแทนครับ', 'warning');
        }
      );
    } else {
      Swal.fire('ไม่รองรับ GPS', 'เบราว์เซอร์ของคุณไม่รองรับการดึงพิกัด GPS', 'error');
    }
  };

  // ตรวจสอบความถูกต้องของข้อมูลก่อนส่ง
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerName.trim()) return Swal.fire('ข้อมูลไม่ครบถ้วน', 'กรุณากรอกชื่อของคุณด้วยครับ', 'warning');
    if (!phone.trim()) return Swal.fire('ข้อมูลไม่ครบถ้วน', 'กรุณากรอกเบอร์โทรศัพท์ที่ติดต่อได้ด้วยครับ', 'warning');
    if (!address.trim()) return Swal.fire('ข้อมูลไม่ครบถ้วน', 'กรุณากรอกที่อยู่ในการดำเนินงานด้วยครับ', 'warning');
    if (!problem.trim()) return Swal.fire('ข้อมูลไม่ครบถ้วน', 'กรุณาระบุรายละเอียดงานหรือปัญหาที่พบด้วยครับ', 'warning');
    if (!appointmentDate) return Swal.fire('ข้อมูลไม่ครบถ้วน', 'กรุณาเลือกวันที่ต้องการนัดหมายด้วยครับ', 'warning');
    if (!hasPinned) return Swal.fire('ยังไม่ได้ปักหมุด', 'กรุณาปักหมุดที่ตั้งในแผนที่นำทางด้วยครับ', 'warning');

    // ตรวจสอบวันคิวเต็ม
    if (blockedDates.includes(appointmentDate)) {
      return Swal.fire('คิวงานเต็ม', 'วันที่คุณเลือกมีคิวงานเต็มแล้วเรียบร้อย กรุณาเลือกวันอื่นครับ', 'warning');
    }

    // ตรวจสอบวันที่เป็นอดีต
    const todayStr = new Date().toISOString().split('T')[0];
    if (appointmentDate < todayStr) {
      return Swal.fire('วันที่ไม่ถูกต้อง', 'ไม่สามารถเลือกวันจองคิวย้อนหลังได้ กรุณาเลือกวันที่ปัจจุบันหรืออนาคตครับ', 'warning');
    }

    setIsSubmitting(true);
    Swal.fire({
      title: 'กำลังบันทึกข้อมูลการจอง...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const payload = {
        customerName: customerName.trim(),
        phone: phone.trim(),
        address: address.trim(),
        problem: problem.trim(),
        price: '', // ว่างไว้ให้แอดมินกำหนด
        mapLink: `${mapPosition.lat.toFixed(6)},${mapPosition.lng.toFixed(6)}`, // ส่งเป็น lat,lng
        technicianId: '', // รอก้ามปูจ่ายงาน
        technicianName: '', // รอก้ามปูจ่ายงาน
        appointmentDate: appointmentDate,
        appointmentTime: '', // ส่งเป็นค่าว่างเพื่อให้ผู้ดูแลระบบจัดคิว
        status: 'pending_approval', // รออนุมัติ
        job_type: 'general',
        referred_by_tech: referredByTech.trim() || null
      };

      const response = await fetch(`${API_URL}?action=add_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        // บันทึกเบอร์โทรศัพท์เพื่อใช้ในการติดตามประวัติ
        localStorage.setItem('customerPhone', phone.trim());

        Swal.fire({
          icon: 'success',
          title: 'จองคิวสำเร็จ! 🎉',
          text: 'คิวงานของคุณส่งเข้าหน้ารออนุมัติเรียบร้อยแล้ว ทีมผู้ดูแลจะรีบติดต่อกลับเพื่อยืนยันเวลานัดหมาย',
          confirmButtonText: 'ติดตามสถานะงาน',
          confirmButtonColor: '#e11d48'
        }).then(() => {
          navigate('/tracking'); // ไปยังหน้าติดตามผลทันที
        });
      } else {
        throw new Error(data.message || 'บันทึกข้อมูลล้มเหลว');
      }
    } catch (error) {
      console.error(error);
      Swal.fire('เกิดข้อผิดพลาด', error.message || 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองอีกครั้ง', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex justify-center p-4 font-sans text-slate-800 relative overflow-hidden">
      
      {/* 🔮 3D Glowing Blurred Background Orbs */}
      <div className="absolute top-[-10%] left-[-15%] w-[60%] aspect-square rounded-full bg-red-500/5 blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-15%] w-[60%] aspect-square rounded-full bg-amber-500/5 blur-[100px] pointer-events-none"></div>

      <style>{`
        .glass-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border: 1px solid rgba(226, 232, 240, 0.8);
        }
        .custom-input {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          color: #1e293b;
          transition: all 0.3s ease;
        }
        .custom-input:focus {
          border-color: #ef4444;
          box-shadow: 0 0 10px rgba(239, 68, 68, 0.15);
          outline: none;
        }
        .custom-input::placeholder {
          color: #94a3b8;
        }
      `}</style>

      <div className="w-full max-w-md space-y-6 relative z-10 flex flex-col justify-start">
        
        {/* Header ส่วนหัว */}
        <div className="text-center mt-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-600 border border-red-200 text-[10px] font-black rounded-full uppercase tracking-wider mb-2">
            🛡️ THUNDER SECURITY SYSTEM
          </div>
          <h1 className="text-2xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-red-650 via-amber-500 to-yellow-600">
            จองคิวบริการแจ้งซ่อม
          </h1>
          <p className="text-amber-600 text-xs font-bold mt-1">THUNDER MAXHOME</p>
        </div>

        {/* ฟอร์มการจอง */}
        <form onSubmit={handleSubmit} className="glass-card p-6 rounded-3xl space-y-4 shadow-2xl">
          
          {/* ข้อมูลลูกค้า */}
          <div className="space-y-3">
            <h3 className="text-xs font-black text-red-600 uppercase tracking-widest border-b border-slate-200 pb-2">📋 ข้อมูลผู้แจ้งซ่อม</h3>
            
            {/* ชื่อลูกค้า */}
            <div>
              <label className="text-[10px] text-slate-500 font-bold block mb-1 flex items-center gap-1">
                <FiUser className="text-red-500" /> ชื่อ-นามสกุล ผู้แจ้ง
              </label>
              <input 
                required
                type="text" 
                placeholder="กรอกชื่อ-นามสกุลของคุณ" 
                className="w-full p-3 rounded-xl text-sm custom-input"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>

            {/* เบอร์โทร */}
            <div>
              <label className="text-[10px] text-slate-500 font-bold block mb-1 flex items-center gap-1">
                <FiPhone className="text-red-500" /> เบอร์โทรศัพท์ติดต่อ
              </label>
              <input 
                required
                type="tel" 
                placeholder="กรอกเบอร์โทรศัพท์ 10 หลัก" 
                className="w-full p-3 rounded-xl text-sm custom-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            {/* ที่อยู่ */}
            <div>
              <label className="text-[10px] text-slate-500 font-bold block mb-1 flex items-center gap-1">
                <FiBookOpen className="text-red-500" /> ที่อยู่สถานที่ดำเนินงาน
              </label>
              <textarea 
                required
                rows="2"
                placeholder="บ้านเลขที่, ถนน, ตำบล, อำเภอ, จังหวัด" 
                className="w-full p-3 rounded-xl text-sm custom-input resize-none"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            {/* รายละเอียดปัญหา */}
            <div>
              <label className="text-[10px] text-slate-500 font-bold block mb-1 flex items-center gap-1">
                <FiBookOpen className="text-red-500" /> รายละเอียดงาน / อาการชำรุด
              </label>
              <textarea 
                required
                rows="2.5"
                placeholder="เช่น แอร์ไม่เย็น น้ำรั่ว มีเสียงดังชำรุด" 
                className="w-full p-3 rounded-xl text-sm custom-input resize-none"
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
              />
            </div>
          </div>

          {/* ปักหมุดแผนที่ */}
          <div className="space-y-2">
            <div className="flex justify-between items-center border-b border-slate-200 pb-2">
              <h3 className="text-xs font-black text-red-600 uppercase tracking-widest flex items-center gap-1">
                <FiMapPin /> ปักหมุดนำทางช่าง
              </h3>
              <button 
                type="button"
                onClick={handleGetCurrentLocation}
                className="inline-flex items-center gap-1 text-[10px] bg-red-50 hover:bg-red-100 text-red-600 font-bold py-1 px-2.5 rounded-lg border border-red-200 transition cursor-pointer"
              >
                <FiCompass /> ตำแหน่งปัจจุบัน
              </button>
            </div>
            
            <p className="text-[9px] text-slate-400 leading-normal mb-1">
              * แตะบนแผนที่เพื่อขยับหมุดไปยังพิกัดหน้างานของท่าน เพื่อให้ทีมช่างเดินทางได้ถูกต้อง
            </p>

            <div className="w-full h-44 rounded-2xl overflow-hidden border border-slate-250 shadow-inner z-0 relative">
              <MapContainer center={[mapPosition.lat, mapPosition.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <LocationMarker 
                  position={hasPinned ? mapPosition : null} 
                  setPosition={(latlng) => {
                    setMapPosition(latlng);
                    setHasPinned(true);
                  }} 
                />
              </MapContainer>
            </div>
            {hasPinned ? (
              <p className="text-[9px] text-emerald-600 font-bold text-center">
                📍 ปักหมุดพิกัดเรียบร้อยแล้ว: {mapPosition.lat.toFixed(6)}, {mapPosition.lng.toFixed(6)}
              </p>
            ) : (
              <p className="text-[9px] text-red-500 font-bold text-center animate-pulse">
                ⚠️ กรุณาคลิกเลือกจุดบนแผนที่เพื่อปักหมุดสถานที่
              </p>
            )}
          </div>

          {/* วันนัดหมายและรหัสแนะนำ */}
          <div className="space-y-3">
            <h3 className="text-xs font-black text-red-600 uppercase tracking-widest border-b border-slate-200 pb-2">📅 วันนัดหมาย</h3>
            
            {/* วันนัดหมาย */}
            <div>
              <label className="text-[10px] text-slate-500 font-bold block mb-1 flex items-center gap-1">
                <FiCalendar className="text-red-500" /> วันที่นัดหมาย
              </label>
              
              {/* ปฏิทินแสดงผลโดยตรง (Light theme) */}
              <div className="mt-2 shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
                <MiniCalendar 
                  selectedDate={appointmentDate} 
                  onChange={(dateStr) => setAppointmentDate(dateStr)} 
                  allowPastDates={false}
                />
              </div>
              
              {appointmentDate && (
                <p className="text-xs text-emerald-600 font-bold mt-2 text-center">
                  ✓ เลือกวันที่นัดหมายแล้ว: {new Date(appointmentDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
              
              <p className="text-[8px] text-slate-400 mt-1.5">
                * แอดมินจะเป็นผู้กำหนดเวลาในการออกปฏิบัติงาน เนื่องจากช่างอาจมีงานและคิวอื่นๆ ทับซ้อน
              </p>
            </div>

            {/* รหัสผู้แนะนำ */}
            <div>
              <label className="text-[10px] text-slate-500 font-bold block mb-1 flex items-center gap-1">
                <FiCornerDownRight className="text-red-500" /> รหัสผู้แนะนำ (พนักงาน/ช่าง) - ถ้ามี
              </label>
              <input 
                disabled={isFromLink}
                type="text" 
                placeholder="เช่น THM-NAME" 
                className={`w-full p-3 rounded-xl text-sm custom-input uppercase font-mono ${isFromLink ? 'opacity-70 bg-slate-50 border-emerald-500/30 text-emerald-700' : ''}`}
                value={referredByTech}
                onChange={(e) => setReferredByTech(e.target.value)}
              />
              {isFromLink && (
                <p className="text-[8px] text-emerald-600 font-bold mt-1">
                  ✓ ตรวจพบรหัสผู้แนะนำอัตโนมัติจากลิงก์
                </p>
              )}
            </div>
          </div>

          {/* ปุ่มบันทึกการจอง */}
          <button 
            disabled={isSubmitting}
            type="submit" 
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-amber-500 hover:from-red-700 hover:to-amber-600 font-black text-sm tracking-wide text-white transition shadow-lg hover:shadow-red-500/10 cursor-pointer border-none mt-2 active:scale-[0.98]"
          >
            {isSubmitting ? 'กำลังบันทึกข้อมูล...' : 'จองคิว'}
          </button>
        </form>

        {/* ปุ่มกลับหน้าหลัก/ติดตามสถานะ */}
        <button 
          onClick={() => navigate('/tracking')}
          className="w-full text-center text-xs text-slate-500 hover:text-slate-800 transition font-bold py-3 underline cursor-pointer bg-transparent border-none"
        >
          กลับหน้าหลัก / ติดตามสถานะงานที่จองไว้
        </button>

      </div>
    </div>
  );
}